/**
 * Action Registry Service
 *
 * Unified API for accessing both system (predefined) and user-defined actions.
 * Handles cloning system actions for editing, and delegates storage to ActionStorage.
 */

import { RigAction, UserRigAction } from '../types/RiggingTypes';
import { ACTION_LIBRARY } from './ActionLibrary';
import { ActionStorage, defaultStorage } from './ActionStorage';

/**
 * Generate a unique ID for new actions.
 */
function generateId(): string {
  return `user_action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * ActionRegistry provides a unified interface for all actions.
 */
class ActionRegistryService {
  private storage: ActionStorage;

  constructor(storage: ActionStorage = defaultStorage) {
    this.storage = storage;
  }

  /**
   * Get all predefined system actions.
   */
  getSystemActions(): RigAction[] {
    return ACTION_LIBRARY;
  }

  /**
   * Get user-defined actions, optionally filtered by video ID.
   */
  getUserActions(videoId?: string): UserRigAction[] {
    return this.storage.load(videoId);
  }

  /**
   * Get all actions (system + user), optionally filtered by video ID.
   */
  getAllActions(videoId?: string): RigAction[] {
    const systemActions = this.getSystemActions();
    const userActions = this.getUserActions(videoId);
    return [...systemActions, ...userActions];
  }

  /**
   * Check if an action is a user-defined action.
   */
  isUserAction(action: RigAction): action is UserRigAction {
    return 'source' in action && (action as UserRigAction).source === 'user';
  }

  /**
   * Save a new user action.
   */
  saveUserAction(action: UserRigAction): void {
    this.storage.save(action);
  }

  /**
   * Create a new user action from recording data.
   */
  createFromRecording(
    name: string,
    boneAnimations: RigAction['boneAnimations'],
    duration: number,
    options?: {
      keywords?: string[];
      description?: string;
      videoId?: string;
    }
  ): UserRigAction {
    const now = new Date().toISOString();
    const action: UserRigAction = {
      id: generateId(),
      name,
      keywords: options?.keywords || [],
      description: options?.description || `Custom recorded action: ${name}`,
      duration,
      boneAnimations,
      source: 'user',
      createdAt: now,
      updatedAt: now,
      createdFrom: 'recording',
      videoId: options?.videoId,
    };
    this.storage.save(action);
    return action;
  }

  /**
   * Clone a system action for editing.
   * Creates a user action with provenance tracking.
   */
  cloneSystemAction(
    systemActionId: string,
    newName?: string
  ): UserRigAction | null {
    const systemAction = ACTION_LIBRARY.find((a) => a.id === systemActionId);
    if (!systemAction) return null;

    const now = new Date().toISOString();
    const action: UserRigAction = {
      ...systemAction,
      id: generateId(),
      name: newName || `${systemAction.name} (Custom)`,
      source: 'user',
      createdAt: now,
      updatedAt: now,
      createdFrom: `system:${systemActionId}` as const,
    };
    this.storage.save(action);
    return action;
  }

  /**
   * Update an existing user action.
   */
  updateUserAction(id: string, updates: Partial<UserRigAction>): void {
    this.storage.update(id, updates);
  }

  /**
   * Delete a user action.
   */
  deleteUserAction(id: string): void {
    this.storage.delete(id);
  }

  /**
   * Get action by ID (searches both system and user).
   */
  getActionById(id: string, videoId?: string): RigAction | undefined {
    // Check system actions first
    const systemAction = ACTION_LIBRARY.find((a) => a.id === id);
    if (systemAction) return systemAction;

    // Check user actions
    const userActions = this.getUserActions(videoId);
    return userActions.find((a) => a.id === id);
  }
}

// Export singleton instance
export const ActionRegistry = new ActionRegistryService();

// Export class for custom instantiation
export { ActionRegistryService };
